import { lazy, PropsWithChildren, Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { Navigate, Outlet, RouteObject, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import yn from "yn";
import AppHeader, { HEADER_HEIGHT } from "~/components/AppHeader";
import Loading from "~/components/Loading";
import { ManifoldNextGenLayout } from "~/components/ManifoldNextGenLayout";
import useAppHeader from "~/contexts/header";
import { useForceManifoldNextGenFeature } from "~/hooks/useManifoldNextGenFeature";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import {
  Admin,
  Cohorts,
  DataBrowser,
  DataOverview,
  Home,
  NotFound,
  ResearchWorkbench,
  Shared,
  SmPortal,
  ShadcnTest,
} from "~/pages";
import AdminDatasetDetail from "~/pages/admin/components/DatasetDetail";
import AdminDatasets from "~/pages/admin/components/Datasets";
import AdminTables from "~/pages/admin/components/Tables";
import FundingSources from "~/pages/admin/components/cost-management/FundingSources";
import QueryInsights from "~/pages/admin/components/query-insights/QueryInsights";
import Groups from "~/pages/admin/components/user-management/Groups";
import Users from "~/pages/admin/components/user-management/Users";
import { AuditLog } from "~/pages/datasets/components/AuditLog";
import { AdminLayout } from "~/pages/manifold-nextgen/components/admin/AdminLayout";
import { AgentBehaviorTab } from "~/pages/manifold-nextgen/components/agents/AgentBehaviorTab";
import { AgentCapabilitiesTab } from "~/pages/manifold-nextgen/components/agents/AgentCapabilitiesTab";
import { AgentConversationsTab } from "~/pages/manifold-nextgen/components/agents/AgentConversationsTab";
import { AgentDetail } from "~/pages/manifold-nextgen/components/agents/AgentDetail";
import { AgentOverviewTab } from "~/pages/manifold-nextgen/components/agents/AgentOverviewTab";
import { AgentSkillsTab } from "~/pages/manifold-nextgen/components/agents/AgentSkillsTab";
import { AgentStarterPromptsTab } from "~/pages/manifold-nextgen/components/agents/AgentStarterPromptsTab";
import { Agents } from "~/pages/manifold-nextgen/components/agents/Agents";
import { AdminConversationRoute } from "~/pages/manifold-nextgen/components/conversations/AdminConversationRoute";
import { ConversationRedirect } from "~/pages/manifold-nextgen/components/conversations/ConversationRedirect";
import { ConversationRoute } from "~/pages/manifold-nextgen/components/conversations/ConversationRoute";
import { ProjectFeaturedSessionRoute } from "~/pages/manifold-nextgen/components/conversations/ProjectFeaturedSessionRoute";
import { SharedConversationRoute } from "~/pages/manifold-nextgen/components/conversations/SharedConversationRoute";
import { DatasetDetail } from "~/pages/manifold-nextgen/components/datasets/DatasetDetail";
import { Datasets } from "~/pages/manifold-nextgen/components/datasets/Datasets";
import { PersonalDatasetDetail } from "~/pages/manifold-nextgen/components/datasets/PersonalDatasetDetail";
import { DatasetAIContext } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetAIContext";
import { DatasetAISkillsTab } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetAISkillsTab";
import { DatasetAccessRequests } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetAccessRequests";
import { DatasetAddTable } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetAddTable";
import { DatasetCohortExplorer } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetCohortExplorer";
import { DatasetDashboards } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetDashboards";
import { DatasetFiles } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetFiles";
import { DatasetOverview } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetOverview";
import { DatasetReport } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetReport";
import { DatasetTableDetail } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetTableDetail";
import { DatasetTables } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetTables";
import { PersonalDatasetFiles } from "~/pages/manifold-nextgen/components/datasets/pages/PersonalDatasetFiles";
import { PersonalDatasetOverview } from "~/pages/manifold-nextgen/components/datasets/pages/PersonalDatasetOverview";
import { PersonalDatasetTables } from "~/pages/manifold-nextgen/components/datasets/pages/PersonalDatasetTables";
import { TurnDiagnostics } from "~/pages/manifold-nextgen/components/diagnostics/TurnDiagnostics";
import EvalCasesPage from "~/pages/manifold-nextgen/components/evals/EvalCasesPage";
import EvalRunDetailPage from "~/pages/manifold-nextgen/components/evals/EvalRunDetailPage";
import EvalRunsPage from "~/pages/manifold-nextgen/components/evals/EvalRunsPage";
import EvalsPage from "~/pages/manifold-nextgen/components/evals/EvalsPage";
import { Home as NextGenHome } from "~/pages/manifold-nextgen/components/home/Home";
import { KnowledgeArticle } from "~/pages/manifold-nextgen/components/kb/KnowledgeArticle";
import { KnowledgeBase } from "~/pages/manifold-nextgen/components/kb/KnowledgeBase";
import { ProjectAppsFeatureRoute } from "~/pages/manifold-nextgen/components/projects/ProjectAppsFeatureRoute";
import { ProjectArtifacts } from "~/pages/manifold-nextgen/components/projects/ProjectArtifacts";
import { ProjectIndex } from "~/pages/manifold-nextgen/components/projects/ProjectIndex";
import { ProjectLayout } from "~/pages/manifold-nextgen/components/projects/ProjectLayout";
import { ProjectOverview } from "~/pages/manifold-nextgen/components/projects/ProjectOverview";
import { ProjectSessionsList } from "~/pages/manifold-nextgen/components/projects/ProjectSessionsList";
import { Projects } from "~/pages/manifold-nextgen/components/projects/Projects";
import { PublishedAppPage } from "~/pages/manifold-nextgen/components/projects/PublishedAppPage";
import { ProjectCosts } from "~/pages/manifold-nextgen/components/projects/costs/ProjectCosts";
import { ProjectFiles } from "~/pages/manifold-nextgen/components/projects/files/ProjectFiles";
import { QuickActionsAdmin } from "~/pages/manifold-nextgen/components/quick-actions/QuickActionsAdmin";
import { SearchManager } from "~/pages/manifold-nextgen/components/search-manager/SearchManager";
import { ProfileAccountTab } from "~/pages/manifold-nextgen/components/users/ProfileAccountTab";
import { ProfileAppearanceTab } from "~/pages/manifold-nextgen/components/users/ProfileAppearanceTab";
import { ProfileMemoriesTab } from "~/pages/manifold-nextgen/components/users/ProfileMemoriesTab";
import { ProfileNotificationsTab } from "~/pages/manifold-nextgen/components/users/ProfileNotificationsTab";
import { ProfilePersonalizationTab } from "~/pages/manifold-nextgen/components/users/ProfilePersonalizationTab";
import { ProfileSkillsTab } from "~/pages/manifold-nextgen/components/users/ProfileSkillsTab";
import { UserProfile } from "~/pages/manifold-nextgen/components/users/UserProfile";
import {
  FLIPT_ROUTE_KEYS,
  ROUTE_ADMIN,
  ROUTE_COST_MANAGEMENT,
  ROUTE_DATASETS,
  ROUTE_NOVA_VIEW,
  ROUTE_SHARED,
  ROUTE_TABLES,
} from "~/router/constants";
import { FliptFeatureRoute } from "./components/FliptFeatureRoute";
import { ProtectedAppRoute } from "./components/ProtectedAppRoute";
import redirects from "./redirects";
import { ROUTE_404 } from "./router/constants";
import {
  ADMIN_AGENTS,
  MANAGE_QUICK_ACTIONS,
  USE_AI_AGENTS,
  USE_PROJECTS,
  VIEW_ADMIN_APP,
  VIEW_ALL_AI_CONVERSATIONS,
  VIEW_SM_APP,
} from "./router/permissions";

interface AppLayoutProps extends PropsWithChildren {
  enabled?: boolean;
}

const {
  VITE_RWB_ENABLED,
  VITE_SM_ENABLED,
  VITE_OUTREACH_API,
  VITE_PARTICIPANT_API,
  VITE_SURVEY_API,
  VITE_DATA_DASHBOARD_URL,
  VITE_ADMIN_APP_ENABLED,
  VITE_COHORT_EXPLORER_ENABLED,
  VITE_CONVERSATION_DIAGNOSTICS_ENABLED,
} = import.meta.env;

const diagnosticsEnabled = yn(VITE_CONVERSATION_DIAGNOSTICS_ENABLED, { default: false });

/**
 * Top-level app layout
 * @param enabled - Build-time, env specific feature flag support
 * @param children
 * @constructor
 */
const AppLayout = ({ enabled = true, children }: AppLayoutProps) => {
  const location = useLocation();
  const wrapperRef = useRef<HTMLElement>(null);
  const { setCenterContent } = useAppHeader();
  const contentHeight = `calc(100vh - ${HEADER_HEIGHT})`;
  const [preferences] = useUserPreferences();
  const { enabled: forceManifoldNextGen } = useForceManifoldNextGenFeature();

  useEffect(() => {
    // Handle resetting scroll on navigation
    wrapperRef.current?.scrollTo({ top: 0 });
  }, [wrapperRef.current, location.pathname]);

  useLayoutEffect(() => {
    // Ugly hack to avoid flicker between tab switches on participant profile
    // TODO: Deprecate in favor of a more generic solution for multi-page center content
    if (!/^\/sm\/participants\/\d+.*$/.test(location.pathname)) {
      // Reset page specific center content when navigating
      setCenterContent(null);
    }
  }, [location.pathname, setCenterContent]);

  if (!enabled) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (
    (preferences.optedIntoManifoldNextGen || forceManifoldNextGen) &&
    !location.pathname.startsWith(ROUTE_NOVA_VIEW) &&
    // Exempt /shared/* so row clicks from ConversationTable land on the classic conversation viewer
    !location.pathname.startsWith(ROUTE_SHARED)
  ) {
    return <Navigate to={ROUTE_NOVA_VIEW} replace />;
  }

  return (
    <QueryParamProvider adapter={ReactRouter6Adapter} options={{ removeDefaultsFromUrl: true }}>
      <AppHeader />
      <Box
        component="main"
        ref={wrapperRef}
        sx={{
          position: "relative",
          height: contentHeight,
          overflow: "auto",
          bgcolor: "common.pageBackground",
        }}
      >
        {children}
      </Box>
    </QueryParamProvider>
  );
};

/**
 * Wrapper for lazy imported apps
 * @param children - Route content which might also include its own sub-router
 * @constructor
 */
const LazyWrapper = ({ children }: PropsWithChildren) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

// Lazy like the other heavy sub-apps below (Cohorts, ResearchWorkbench, etc.) —
// it mounts RWB's whole project route module (Notepad, Environments, Worksheets,
// Workflows, Pipeline, + their MUI deps), which must stay code-split rather
// than join this eager route bundle every user pays for.
const ProjectApps = lazy(() => import("~/pages/manifold-nextgen/components/projects/ProjectApps"));

const routes: RouteObject[] = [
  {
    index: true,
    element: (
      <AppLayout>
        <Home />
      </AppLayout>
    ),
  },
  ...redirects,
  {
    path: "shadcn-test",
    element: (
      <AppLayout enabled={import.meta.env.DEV}>
        <ShadcnTest />
      </AppLayout>
    ),
  },
  // Note: Global files browser is now under /data-browser/files
  // The redirect from /files to /data-browser/files is in redirects.tsx
  {
    path: "cohorts/*",
    element: (
      <AppLayout enabled={yn(VITE_COHORT_EXPLORER_ENABLED, { default: false })}>
        <LazyWrapper>
          <Cohorts />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "workbenches/*",
    element: (
      <AppLayout enabled={yn(VITE_RWB_ENABLED, { default: false })}>
        <LazyWrapper>
          <ResearchWorkbench />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "data-browser/*",
    element: (
      <AppLayout>
        <DataBrowser />
      </AppLayout>
    ),
  },
  {
    path: "data-overview",
    element: (
      <AppLayout enabled={!!VITE_DATA_DASHBOARD_URL}>
        <DataOverview />
      </AppLayout>
    ),
  },
  {
    path: "sm/*",
    element: (
      <AppLayout enabled={yn(VITE_SM_ENABLED, { default: false })}>
        <LazyWrapper>
          <ProtectedAppRoute permission={VIEW_SM_APP}>
            <SmPortal />
          </ProtectedAppRoute>
        </LazyWrapper>
      </AppLayout>
    ),
    handle: {
      serverHealth: [
        {
          endpoint: VITE_OUTREACH_API,
          label: "Outreach Service",
        },
        {
          endpoint: VITE_PARTICIPANT_API,
          label: "Participant Service",
        },
        {
          endpoint: VITE_SURVEY_API,
          label: "Survey Service",
        },
      ],
    },
  },
  {
    path: "admin/*",
    element: (
      <AppLayout enabled={yn(VITE_ADMIN_APP_ENABLED, { default: false })}>
        <LazyWrapper>
          <Admin />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "shared/*",
    element: (
      <AppLayout>
        <LazyWrapper>
          <Shared />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "nova",
    element: <ManifoldNextGenLayout />,
    children: [
      {
        index: true,
        element: <NextGenHome />,
      },
      {
        path: "agents",
        element: (
          <ProtectedAppRoute permission={USE_AI_AGENTS}>
            <Agents />
          </ProtectedAppRoute>
        ),
      },
      {
        path: "datasets",
        element: <Datasets />,
      },
      {
        path: "datasets/personal",
        element: <PersonalDatasetDetail />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <PersonalDatasetOverview /> },
          { path: "tables", element: <PersonalDatasetTables /> },
          { path: "files", element: <PersonalDatasetFiles /> },
        ],
      },
      {
        path: "kb",
        element: <KnowledgeBase />,
      },
      {
        path: "kb/:slug",
        element: <KnowledgeArticle />,
      },
      {
        path: "profile",
        element: <UserProfile />,
        children: [
          { index: true, element: <Navigate to="account" replace /> },
          { path: "account", element: <ProfileAccountTab /> },
          { path: "appearance", element: <ProfileAppearanceTab /> },
          { path: "notifications", element: <ProfileNotificationsTab /> },
          { path: "personalization", element: <ProfilePersonalizationTab /> },
          { path: "memories", element: <ProfileMemoriesTab /> },
          { path: "skills", element: <ProfileSkillsTab /> },
        ],
      },
      {
        path: "datasets/:datasetId",
        element: <DatasetDetail />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <DatasetOverview /> },
          { path: "dashboards", element: <DatasetDashboards /> },
          { path: "dashboards/:reportId", element: <DatasetReport /> },
          {
            path: "tables",
            children: [
              { index: true, element: <DatasetTables /> },
              { path: "new", element: <DatasetAddTable /> },
              { path: ":tableFQN", element: <DatasetTableDetail /> },
            ],
          },
          { path: "files", element: <DatasetFiles /> },
          { path: "files/:prefixId/*", element: <DatasetFiles /> },
          { path: "ai-context", element: <DatasetAIContext /> },
          { path: "ai-skills", element: <DatasetAISkillsTab /> },
          { path: "cohort-explorer", element: <DatasetCohortExplorer /> },
          { path: "access-requests", element: <DatasetAccessRequests /> },
          { path: "audit-log", element: <AuditLog /> },
        ],
      },
      {
        path: "agents/:agentId",
        element: <AgentDetail />,
        children: [
          {
            index: true,
            element: <Navigate to="overview" replace />,
          },
          {
            path: "overview",
            element: <AgentOverviewTab />,
          },
          {
            path: "behavior",
            element: <AgentBehaviorTab />,
          },
          {
            path: "skills",
            element: <AgentSkillsTab />,
          },
          {
            path: "capabilities",
            element: <AgentCapabilitiesTab />,
          },
          {
            path: "guide",
            element: <AgentStarterPromptsTab />,
          },
          {
            path: "sessions",
            element: <AgentConversationsTab />,
          },
        ],
      },
      {
        path: "projects",
        element: (
          <ProtectedAppRoute permission={USE_PROJECTS}>
            <Projects />
          </ProtectedAppRoute>
        ),
      },
      // Outside `admin/`: searching runs as the caller, so `view:admin-app` would
      // gate a user page on an admin role. The page hides its index tab without
      // the AI admin permission.
      {
        path: "search-manager",
        element: (
          <FliptFeatureRoute flagKey={FLIPT_ROUTE_KEYS.AI_SEARCH_MANAGER}>
            <ProtectedAppRoute permission={USE_AI_AGENTS}>
              <SearchManager />
            </ProtectedAppRoute>
          </FliptFeatureRoute>
        ),
      },
      { path: "c/:conversationId", element: <ConversationRedirect /> },
      {
        // The project's own id is `:nextgenProjectId`, not `:projectId`, so the
        // embedded Research Workbench subtree below can claim `:projectId` for
        // the workbench id its ~40 `useParams()` call sites already read.
        path: "projects/:nextgenProjectId",
        element: <ProjectLayout />,
        children: [
          { index: true, element: <ProjectOverview /> },
          { path: "c", element: <ProjectIndex /> },
          { path: "artifacts", element: <ProjectArtifacts /> },
          { path: "files/*", element: <ProjectFiles /> },
          { path: "costs", element: <ProjectCosts /> },
          { path: "sessions", element: <ProjectSessionsList /> },
          { path: "c/:conversationId", element: <ConversationRoute /> },
          { path: "featured/:token", element: <ProjectFeaturedSessionRoute /> },
          {
            path: "a/:appSlug",
            element: (
              <ProjectAppsFeatureRoute>
                <PublishedAppPage />
              </ProjectAppsFeatureRoute>
            ),
          },
          {
            path: "apps/:projectId/*",
            element: (
              <LazyWrapper>
                <ProjectApps />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "shared/c/:token",
        element: <SharedConversationRoute />,
      },
      {
        path: "admin",
        element: (
          <ProtectedAppRoute permission={VIEW_ADMIN_APP}>
            <AdminLayout />
          </ProtectedAppRoute>
        ),
        children: [
          { index: true, element: <Navigate to="users" replace /> },
          { path: "users", element: <Users isNextGen /> },
          { path: "groups", element: <Groups isNextGen /> },
          // N.B. the parent already requires `view:admin-app`; these keep their own
          // checks because each page is gated on a different permission, so they
          // stay independent if the roles holding them ever diverge.
          {
            path: ROUTE_COST_MANAGEMENT,
            element: (
              <ProtectedAppRoute path={`${ROUTE_ADMIN}/${ROUTE_COST_MANAGEMENT}`}>
                <FundingSources isNextGen />
              </ProtectedAppRoute>
            ),
          },
          {
            path: ROUTE_DATASETS,
            element: (
              <ProtectedAppRoute path={`${ROUTE_ADMIN}/${ROUTE_DATASETS}`}>
                <AdminDatasets isNextGen />
              </ProtectedAppRoute>
            ),
          },
          {
            path: `${ROUTE_DATASETS}/:datasetId`,
            element: (
              <ProtectedAppRoute path={`${ROUTE_ADMIN}/${ROUTE_DATASETS}`}>
                <AdminDatasetDetail isNextGen />
              </ProtectedAppRoute>
            ),
          },
          {
            path: ROUTE_TABLES,
            element: (
              <ProtectedAppRoute path={`${ROUTE_ADMIN}/${ROUTE_TABLES}`}>
                <AdminTables isNextGen />
              </ProtectedAppRoute>
            ),
          },
          {
            path: "quick-actions",
            element: (
              <ProtectedAppRoute permission={MANAGE_QUICK_ACTIONS}>
                <QuickActionsAdmin />
              </ProtectedAppRoute>
            ),
          },
          // Evals are the AI team's own quality surface, so they sit behind
          // `admin:agents` rather than the `manage:agents` the eval API itself
          // requires: every case and run in here belongs to another user's
          // agent, and reading them is a moderation act, not ordinary use. The
          // `ai-evals-dashboard` flag narrows that further while the dashboard
          // is still being shaped. Both gates are stated once, on the layout.
          {
            path: "evals",
            element: (
              <FliptFeatureRoute flagKey={FLIPT_ROUTE_KEYS.AI_EVALS_DASHBOARD}>
                <ProtectedAppRoute permission={ADMIN_AGENTS}>
                  <Outlet />
                </ProtectedAppRoute>
              </FliptFeatureRoute>
            ),
            children: [
              { index: true, element: <EvalsPage /> },
              { path: "cases", element: <EvalCasesPage /> },
              { path: "runs", element: <EvalRunsPage /> },
              { path: "runs/:runId", element: <EvalRunDetailPage /> },
            ],
          },
          ...(diagnosticsEnabled
            ? [
                {
                  path: "turn-diagnostics",
                  element: (
                    <ProtectedAppRoute permission={VIEW_ALL_AI_CONVERSATIONS}>
                      <TurnDiagnostics />
                    </ProtectedAppRoute>
                  ),
                },
              ]
            : []),
          {
            path: "query-insights",
            element: <QueryInsights isNextGen />,
          },
        ],
      },
      {
        path: "admin/c/:conversationId",
        element: (
          <ProtectedAppRoute permission={VIEW_ALL_AI_CONVERSATIONS}>
            <AdminConversationRoute />
          </ProtectedAppRoute>
        ),
      },
    ],
  },
  { path: "404", element: <NotFound /> },
  { path: "*", element: <Navigate to={ROUTE_404} replace /> },
];

export default routes;
